import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface StrategyPerformance {
  total_trades: number;
  win_rate: number;
  net_profit: number;
  winning_trades: number;
  losing_trades: number;
  max_drawdown: number;
  last_updated: string | null;
}

export interface ActiveStrategy {
  id: string;
  strategy_name: string;
  status: string;
  risk_mode: string;
  created_at: string;
  strategy_performance: StrategyPerformance[];
}

interface UseActiveStrategiesReturn {
  strategies: ActiveStrategy[];
  loading: boolean;
  refetch: () => void;
  totalPnl: number;
  avgWinRate: number;
  activeCount: number;
  totalTrades: number;
}

export function useActiveStrategies(): UseActiveStrategiesReturn {
  const { user } = useAuth();
  const [strategies, setStrategies] = useState<ActiveStrategy[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStrategies = useCallback(async () => {
    if (!user?.id) {
      setStrategies([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('strategy_instances')
      .select(`
        id,
        strategy_name,
        status,
        risk_mode,
        created_at,
        strategy_performance (
          total_trades,
          win_rate,
          net_profit,
          winning_trades,
          losing_trades,
          max_drawdown,
          last_updated
        )
      `)
      .eq('user_id', user.id)
      .in('status', ['running', 'paused'])
      .order('created_at', { ascending: false });

    if (!error && data) {
      setStrategies(data as unknown as ActiveStrategy[]);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  // Realtime subscription
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('strategy-instances-dashboard')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'strategy_instances',
          filter: `user_id=eq.${user.id}`,
        },
        () => fetchStrategies()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'strategy_performance',
        },
        () => fetchStrategies()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchStrategies]);

  // Compute aggregates
  const totalPnl = strategies.reduce((sum, s) => {
    const perf = s.strategy_performance?.[0];
    return sum + (perf?.net_profit ?? 0);
  }, 0);

  const activeCount = strategies.filter(s => s.status === 'running').length;

  const totalTrades = strategies.reduce((sum, s) => {
    const perf = s.strategy_performance?.[0];
    return sum + (perf?.total_trades ?? 0);
  }, 0);

  const avgWinRate = strategies.length > 0
    ? strategies.reduce((sum, s) => {
        const perf = s.strategy_performance?.[0];
        return sum + (perf?.win_rate ?? 0);
      }, 0) / strategies.length
    : 0;

  return {
    strategies,
    loading,
    refetch: fetchStrategies,
    totalPnl,
    avgWinRate,
    activeCount,
    totalTrades,
  };
}
